<template>
    <Modal
        class="shuttles-modal"
        :class="{ 'has-error': hasError }"
        :is-open="isOpen"
        animation-name="up"
        @modal-closed="handleClose"
    >
        <template #content>
            <div class="sf-redirect-modal">
                <button type="button" class="modal-close" @click="handleClose" aria-label="Close modal">
                    <span>×</span>
                </button>

                <template v-if="isLoading">
                    <div class="loader" aria-hidden="true">
                        <span class="ball"></span>
                        <span class="ball"></span>
                        <span class="ball"></span>
                    </div>

                    <h3 class="title">
                        {{ t('forms.shuttles.redirectModal.title') }}
                    </h3>

                    <p class="desc">
                        {{ t('forms.shuttles.redirectModal.description') }}
                    </p>

                    <div class="brand">
                        <span>{{ t('forms.shuttles.redirectModal.poweredBy') }}</span>
                        <SfLogo
                            v-if="showLogo"
                            class="h-5 w-auto -mt-0.5"
                            :aria-label="t('forms.shuttles.redirectModal.partnerName')"
                        />
                        <template v-else>
                            <a href="https://www.shuttlefinder.com/" rel="nofollow">
                                {{ t('forms.shuttles.redirectModal.partnerName') }}
                            </a>
                        </template>
                    </div>

                    <div class="secure">
                        <img
                            :src="secureImg"
                            :alt="t('forms.shuttles.redirectModal.secureIconAlt')"
                            class="sf-redirect-modal__image"
                        />
                        <span>{{ t('forms.shuttles.redirectModal.secureConnection') }}</span>
                    </div>
                </template>

                <template v-else-if="hasError">
                    <div class="error-icon-wrapper" role="img" aria-label="Error">
                        <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="error-icon">
                            <path
                                d="M10 20C4.47715 20 0 15.5228 0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20ZM10 18C14.4183 18 18 14.4183 18 10C18 5.58172 14.4183 2 10 2C5.58172 2 2 5.58172 2 10C2 14.4183 5.58172 18 10 18ZM9 13H11V15H9V13ZM9 5H11V11H9V5Z"
                                fill="#D92D20"
                            />
                        </svg>
                    </div>

                    <h3 class="error-title">
                        {{ errorTitle }}
                    </h3>

                    <p class="desc">
                        {{ errorMessage }}
                    </p>

                    <div class="brand">
                        <span>{{ t('forms.shuttles.redirectModal.poweredBy') }}</span>
                        <SfLogo
                            v-if="showLogo"
                            class="h-5 w-auto -mt-0.5"
                            :aria-label="t('forms.shuttles.redirectModal.partnerName')"
                        />
                        <template v-else>
                            <a href="https://www.shuttlefinder.com/" rel="nofollow">
                                {{ t('forms.shuttles.redirectModal.partnerName') }}
                            </a>
                        </template>
                    </div>

                    <div class="secure">
                        <img
                            :src="secureImg"
                            :alt="t('forms.shuttles.redirectModal.secureIconAlt')"
                            class="sf-redirect-modal__image"
                        />
                        <span>{{ t('forms.shuttles.redirectModal.secureConnection') }}</span>
                    </div>
                </template>
            </div>
        </template>
    </Modal>
</template>

<script setup lang="ts">
import { defineAsyncComponent } from 'vue';
import Modal from '@nuxt-monorepo/ui/components/modals/Modal.vue';
import { useModalStore } from '@nuxt-monorepo/base/app/store/modal';
import secureImg from '@nuxt-monorepo/ui/assets/images/secure.svg';

const SfLogo = defineAsyncComponent(() => import('~/../../apps/sf/app/components/Logo/Main.vue'));

interface Props {
    isOpen: boolean;
    isLoading?: boolean;
    hasError?: boolean;
    showLogo?: boolean;
    errorTitle?: string;
    errorMessage?: string;
}

const props = withDefaults(defineProps<Props>(), {
    isLoading: false,
    hasError: false,
    showLogo: false,
    errorTitle: 'Search Failed',
    errorMessage: "We couldn't connect to our booking partner. Please try again in a few moments.",
});

const emit = defineEmits<{
    (e: 'close'): void;
}>();

const { t } = useI18n();
const modalStore = useModalStore();

watch(
    () => props.isOpen,
    (isOpen) => {
        if (isOpen) {
            modalStore.disableScroll();
        } else {
            modalStore.enableScroll();
        }
    },
    { immediate: true }
);

onUnmounted(() => {
    modalStore.enableScroll();
});

const handleClose = () => {
    emit('close');
};
</script>

<style scoped lang="postcss">
.shuttles-modal {
    :deep(.modal-wrapper) {
        @apply fixed inset-0 flex items-center justify-center;
        @apply md:relative md:w-auto md:h-auto md:mt-[10%] md:inset-auto;

        .modal-content {
            @apply w-[90vw] max-w-[386px] bg-white px-4 pt-8 pb-6;
            border-radius: 6px;
            @apply md:w-[386px] md:h-auto;
        }
    }

    :deep(.background) {
        background-color: rgba(40, 44, 74, 0.25) !important;
    }

    &.has-error {
        :deep(.modal-wrapper .modal-content) {
            @apply pt-6;
        }
    }
}

.sf-redirect-modal {
    @apply relative mx-auto w-full flex flex-col justify-center text-center
    md:h-auto md:block;
}

.sf-redirect-modal .loader {
    @apply mb-3 flex items-center justify-center gap-1.5;
}

.sf-redirect-modal .ball {
    @apply h-2 w-2 rounded-full;
    animation: sf-bounce 0.45s infinite alternate;
}

.sf-redirect-modal .ball:nth-child(1) {
    background: #b5e171;
    animation-delay: 0s;
}

.sf-redirect-modal .ball:nth-child(2),
.sf-redirect-modal .ball:nth-child(3) {
    background: #0a475a;
}

.sf-redirect-modal .ball:nth-child(2) {
    animation-delay: 0.1s;
}

.sf-redirect-modal .ball:nth-child(3) {
    animation-delay: 0.2s;
}

@keyframes sf-bounce {
    from {
        transform: translateY(0);
    }
    to {
        transform: translateY(-5px);
    }
}

.sf-redirect-modal .title {
    @apply mb-2 text-lg font-semibold text-[#6B7280];
}

.sf-redirect-modal .desc {
    @apply mb-3 font-normal text-base leading-relaxed text-wl-base-dark;
}

.sf-redirect-modal .brand {
    @apply mb-6 flex items-center justify-center gap-1.5 text-sm font-medium tracking-wide text-slate-500;
}

.sf-redirect-modal .secure {
    @apply inline-flex items-center justify-center gap-0.5 text-sm font-medium text-[#347F1B];
}

.error-icon-wrapper {
    @apply mb-2 flex items-center justify-center;
}

.error-icon {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
}

.error-title {
    @apply mb-2 text-base font-semibold;
    color: #d92d20;
}

.modal-close {
    @apply absolute -top-2 right-2 w-4 h-4 flex items-center justify-center
    text-gray-400 hover:text-gray-600 transition-colors
    bg-transparent border-0 cursor-pointer;
    font-size: 22px;
    line-height: 1;
    font-weight: 300;
}

.modal-close:hover {
    @apply text-gray-700;
}

.modal-close span {
    display: block;
    line-height: 1;
}
</style>
